import { Response } from "@/types"
import { FetchMethod, useCodeerApi } from "../useCodeerApi"

// API response interface (snake_case from backend)
export interface LLMModelResponse {
  id: number
  display_name: string
  model_id: string
  provider: string
  model_type: string
  input_modalities: string[]
  input_credits_per_million_tokens?: string | number | null
  output_credits_per_million_tokens?: string | number | null
  created_at: string
}

// Frontend data interface (camelCase)
export interface LLMModel {
  id: number
  displayName: string
  modelId: string
  provider: string
  modelType: string
  inputModalities: string[]
  // Credits per 1M tokens (margin included). Optional: 0/undefined means
  // pricing is not configured and should not be displayed.
  inputCreditsPerMillionTokens?: number
  outputCreditsPerMillionTokens?: number
  createdAt: string
}

export type AvailableModelsResponse = Response<LLMModelResponse[]>

const parseCredits = (value?: string | number | null): number | undefined => {
  if (value == null) return undefined
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : undefined
}

/**
 * Transform API response to frontend data format
 */
export const transformLLMModel = (response: LLMModelResponse): LLMModel => ({
  id: response.id,
  displayName: response.display_name,
  modelId: response.model_id,
  provider: response.provider,
  modelType: response.model_type,
  inputModalities: response.input_modalities || ["text"],
  inputCreditsPerMillionTokens: parseCredits(
    response.input_credits_per_million_tokens
  ),
  outputCreditsPerMillionTokens: parseCredits(
    response.output_credits_per_million_tokens
  ),
  createdAt: response.created_at,
})

/**
 * Hook to fetch the list of available LLM models from the backend.
 */
export const useGetAvailableModelsApi = () => {
  return useCodeerApi<AvailableModelsResponse>("/llm/models", FetchMethod.Get)
}
